// src/components/RoleSelection.js
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/firebase';
import { doc, setDoc, collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';

const RoleSelection = () => {
  const [userType, setUserType] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceCode, setWorkspaceCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { refreshUserDetails } = useAuth();

  const generateCommunityCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      const user = auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user found');
      }

      if (userType === 'employer') {
        if (!workspaceName.trim()) {
          throw new Error('Please enter a workspace name');
        }

        // Create community
        const communityCode = generateCommunityCode();
        console.log('Creating community with code:', communityCode);
        
        const communityRef = await addDoc(collection(db, 'communities'), {
          code: communityCode,
          name: workspaceName.trim(),
          ownerId: user.uid,
          ownerName: user.displayName || user.email,
          createdAt: new Date().toISOString()
        });

        // Create employer user document
        const userData = {
          email: user.email,
          name: user.displayName || user.email,
          userType: 'employer',
          communityId: communityRef.id,
          communityCode: communityCode,
          workspaceName: workspaceName.trim(),
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString()
        };

        await setDoc(doc(db, 'users', user.uid), userData);
        console.log('Employer document created');

        // Refresh user details before navigating
        await refreshUserDetails();
        navigate('/home', { replace: true });

      } else if (userType === 'employee') {
        if (!workspaceCode) {
          throw new Error('Please enter the workspace code');
        }

        // Validate workspace code
        const communitiesRef = collection(db, 'communities');
        const q = query(communitiesRef, where('code', '==', workspaceCode));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          throw new Error('Invalid workspace code. Please check and try again.');
        }

        const communityDoc = querySnapshot.docs[0];
        const communityData = communityDoc.data();

        // Create employee user document
        const userData = {
          email: user.email,
          name: user.displayName || user.email,
          userType: 'employee',
          communityId: communityDoc.id,
          communityCode: workspaceCode,
          workspaceName: communityData.name,
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString()
        };

        await setDoc(doc(db, 'users', user.uid), userData);
        console.log('Employee document created');

        // Refresh user details before navigating
        await refreshUserDetails();
        navigate('/home', { replace: true });
      }
    } catch (error) {
      console.error('Submission error:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 bg-light d-flex align-items-center">
      <Container className="py-5">
        <div className="mx-auto" style={{ maxWidth: '400px' }}>
          <Card>
            <Card.Body className="p-4">
              {error && (
                <Alert variant="danger">
                  {error}
                </Alert>
              )}
              
              <h2 className="text-center mb-4">Complete Your Profile</h2>
              
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>I am a:</Form.Label>
                  <Form.Select
                    value={userType}
                    onChange={(e) => setUserType(e.target.value)}
                    disabled={loading}
                    required
                  >
                    <option value="">Select role</option>
                    <option value="employer">Employer</option>
                    <option value="employee">Employee</option>
                  </Form.Select>
                </Form.Group>

                {userType === 'employer' && (
                  <Form.Group className="mb-3">
                    <Form.Label>Workspace Name:</Form.Label>
                    <Form.Control
                      type="text"
                      value={workspaceName}
                      onChange={(e) => setWorkspaceName(e.target.value)}
                      placeholder="Enter workspace name"
                      disabled={loading}
                      required
                    />
                  </Form.Group>
                )}

                {userType === 'employee' && (
                  <Form.Group className="mb-3">
                    <Form.Label>Workspace Code:</Form.Label>
                    <Form.Control
                      type="text"
                      value={workspaceCode}
                      onChange={(e) => setWorkspaceCode(e.target.value.toUpperCase())}
                      placeholder="Enter 6-digit code"
                      maxLength={6}
                      disabled={loading}
                      required
                    />
                    <Form.Text className="text-muted">
                      Enter the 6-digit code provided by your employer
                    </Form.Text>
                  </Form.Group>
                )}

                <Button
                  type="submit"
                  className="w-100"
                  disabled={loading || !userType || (userType === 'employer' && !workspaceName.trim()) || (userType === 'employee' && !workspaceCode)}
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Processing...
                    </>
                  ) : (
                    'Complete Setup'
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </div>
  );
};

export default RoleSelection;